import moment from 'moment'

import type { CorrelationFilter } from './analytics-correlation-filter/analytics-correlation-filter.component'
import type { CorrelationSetting } from './analytics-correlation-setting/analytics-correlation-setting.component'
import type { AnalyticsCorrelationService, IAccountData } from './analytics-correlation.service'

import { app } from '../../app.module'

class AnalyticsCorrelationCtrl {
  private snsType: string
  // 相関フィルター
  private filter: CorrelationFilter
  // 相関グラフ設定
  private graphSetting: CorrelationSetting
  // アカウント一覧
  private accounts: IAccountData[]
  private isLoading: boolean
  private csvFilename: string

  constructor(
    $scope,
    private $rootScope,
    private AccountService,
    private AnalyticsCorrelationService: AnalyticsCorrelationService
  ) {}

  public $onInit() {
    this.accounts = []
    this.filter = {
      categoryName: null,
      subCategoryName: null,
      accountTagFilter: null,
      followerRangeFilter: null,
      accountGlobalFilter: null,
      startEndDate: null,
    }
  }

  /**
   * 相関設定変更イベント
   * @param {CorrelationSetting} setting 相関設定
   */
  public onChangeCorrelationSetting(graphSetting: CorrelationSetting) {
    this.graphSetting = graphSetting
  }

  /**
   * CSVダウンロード
   */
  public downloadCsv() {
    this.csvFilename = 'analytics_correlation_' + moment().format('YYYY-MM-DD') + '.csv'
    // CSVデータ
    const csvData = []
    // 対象データをセット
    let objectTitle = 'アカウント名'
    let accounts = []
    switch (this.graphSetting.object) {
      case 'account':
        accounts = this.accounts
        objectTitle = 'アカウント名'
        break
      case 'brandCategory':
        accounts = this.AnalyticsCorrelationService.setBrandCategoryData(this.accounts)
        objectTitle = '大カテゴリ'
        break
      case 'brandSubCategory':
        accounts = this.AnalyticsCorrelationService.setBrandSubCategoryData(this.accounts)
        objectTitle = '小カテゴリ'
        break
    }
    // Y軸の多い順に上からソートする
    let sortBy = this.graphSetting.yAxis.value
    // CSVデータに格納
    let yText = this.graphSetting.yAxis.text
    let xText = this.graphSetting.xAxis.text
    let zText = this.graphSetting.zAxis.text
    let yValue = this.graphSetting.yAxis.value
    let xValue = this.graphSetting.xAxis.value
    let zValue = this.graphSetting.zAxis.value
    // データ投稿あたりフラグがある場合は、セット
    if (this.graphSetting.dataPerPostFlg === true) {
      if (this.AnalyticsCorrelationService.dataPerPostExclude.indexOf(yValue) === -1) {
        yText = yText + '(1投稿あたり)'
        sortBy = sortBy + 'PerPost'
        yValue = yValue + 'PerPost'
      }
      if (this.AnalyticsCorrelationService.dataPerPostExclude.indexOf(xValue) === -1) {
        xText = xText + '(1投稿あたり)'
        xValue = xValue + 'PerPost'
      }
      if (this.AnalyticsCorrelationService.dataPerPostExclude.indexOf(zValue) === -1) {
        zText = zText + '(1投稿あたり)'
        zValue = zValue + 'PerPost'
      }
    }
    // Y軸の多い順に上からソートする
    accounts = this.AnalyticsCorrelationService.sortAccountData(accounts, sortBy, 0)
    let header = []
    // 大カテゴリまたは小カテゴリの場合は、アカウント数列を追加
    if (
      this.graphSetting.object === 'brandCategory' ||
      this.graphSetting.object === 'brandSubCategory'
    ) {
      header = [objectTitle, 'アカウント数', yText, xText, zText]
    } else {
      header = [objectTitle, yText, xText, zText]
    }
    csvData.push(header)
    accounts.forEach((account) => {
      // 大カテゴリまたは小カテゴリの場合は、アカウント数列を追加
      if (
        this.graphSetting.object === 'brandCategory' ||
        this.graphSetting.object === 'brandSubCategory'
      ) {
        csvData.push([
          account.name,
          account.accounts,
          account[yValue],
          account[xValue],
          account[zValue],
        ])
      } else {
        csvData.push([account.name, account[yValue], account[xValue], account[zValue]])
      }
    })
    return csvData
  }

  /**
   * 相関フィルター変更イベント
   * @param {CorrelationFilter} filter
   */
  public onChangeCorrelationFilter(filter: CorrelationFilter) {
    // カテゴリまたは期間を変更する場合は、アカウント一覧を取得
    if (
      this.filter.categoryName !== filter.categoryName ||
      this.filter.startEndDate !== filter.startEndDate
    ) {
      // フィルターをセット
      this.setFilter(filter)
      // アカウントデータの取得
      this.getAccounts()
      return
    }
    // フィルターをセット
    this.setFilter(filter)
    // アカウント一覧をフィルタリング
    this.filterAccount()
  }

  /**
   * フィルターをセット
   * @private
   * @param {CorrelationFilter} filter
   */
  private setFilter(filter: CorrelationFilter): void {
    this.filter = {
      categoryName: filter.categoryName,
      subCategoryName: filter.subCategoryName,
      accountTagFilter: filter.accountTagFilter,
      followerRangeFilter: filter.followerRangeFilter,
      accountGlobalFilter: filter.accountGlobalFilter,
      startEndDate: filter.startEndDate,
    }
  }

  /**
   * アカウントデータの取得
   * @private
   * @return {void}
   */
  private getAccounts(): void {
    this.isLoading = true
    const getOptions = {
      startEndDate: this.filter.startEndDate,
      categoryName: this.filter.categoryName,
      isPro: this.$rootScope.isPro,
    }
    this.AccountService.Account.getAccounts(
      this.$rootScope.groupId,
      this.$rootScope.userId,
      this.snsType,
      getOptions
    ).then((accountDatas) => {
      // アカウント一覧をセット
      this.AnalyticsCorrelationService.setAccounts(this.snsType, accountDatas)
      // アカウント一覧をフィルタリング
      this.filterAccount()
      this.isLoading = false
    })
  }

  /**
   * アカウント一覧をフィルタリング
   * @private
   */
  private filterAccount(): void {
    const filter = {
      subCategoryName: this.filter.subCategoryName,
      accountTagFilter: this.filter.accountTagFilter,
      followerRangeFilter: this.filter.followerRangeFilter,
      accountGlobalFilter: this.filter.accountGlobalFilter,
    }
    this.AnalyticsCorrelationService.filterAccount(filter)
    this.accounts = this.AnalyticsCorrelationService.filteredAccounts
  }
}

app.component('analyticsCorrelation', {
  bindings: {
    snsType: '<',
  },
  template: require('./analytics-correlation.pug')(),
  controller: AnalyticsCorrelationCtrl,
})
